import { ReactElement, useMemo, useState } from 'react'
import { useFieldArray } from 'react-hook-form'
import { Typography, AccordionDetails, Stack, Grid, Alert } from '@mui/material'
import { FormAccordion, TextField, UnitConvertedField } from '@ui/forms'
import { useModelStore, roofStoreyKey } from '@editorStores'
import { windDirectionToAxis } from '../../constants'
import RoofFormFields from '../RoofFormFields'
import StoreyHeader from '../StoreyHeader'
import { buildSlabToStoreyMapping, sortWindLoadResultIndicesByStorey } from './utils'

interface Props {
  direction: WindDirection
}

const FormFields = ({ direction }: Props): ReactElement => {
  const model = useModelStore(state => state.model)
  const [activeStorey, setActiveStorey] = useState<number | typeof roofStoreyKey | null>(null)

  const fields = useFieldArray({
    name: `results.wind_loads_per_ceiling[${direction}]`,
  }).fields as unknown as WindloadCeilingResult[]

  const slabToStorey = useMemo(() => buildSlabToStoreyMapping(model.slabs), [model.slabs])

  const displayIndices = useMemo(
    () => sortWindLoadResultIndicesByStorey(fields, slabToStorey),
    [fields, slabToStorey],
  )

  return (
    <>
      <RoofFormFields
        direction={direction}
        expanded={activeStorey === roofStoreyKey}
        onExpand={expanded => setActiveStorey(expanded ? roofStoreyKey : null)}
      />
      {displayIndices.map(index => {
        const result = fields[index]
        return (
          <FormAccordion
            key={result.slab_guid}
            fields={index.toString()}
            expanded={index === activeStorey}
            onChange={(_, expanded) => setActiveStorey(expanded ? index : null)}
          >
            <StoreyHeader
              resultFieldName="results.wind_loads_per_ceiling"
              direction={direction}
              index={index}
              storey={slabToStorey[result.slab_guid]}
            />
            <AccordionDetails>
              <Stack direction="column" spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <UnitConvertedField
                      label={
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          Punktlast x
                        </Typography>
                      }
                      name={`results.wind_loads_per_ceiling[${direction}][${index}].force.x`}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <UnitConvertedField
                      label={
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          Punktlast y
                        </Typography>
                      }
                      name={`results.wind_loads_per_ceiling[${direction}][${index}].force.y`}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <UnitConvertedField
                      conversionFactor={1}
                      unitLabel={'m'}
                      disabled
                      label={
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          Angriffspunkt x
                        </Typography>
                      }
                      name={`results.wind_loads_per_ceiling[${direction}][${index}].point_of_attack.x`}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <UnitConvertedField
                      conversionFactor={1}
                      unitLabel={'m'}
                      disabled
                      label={
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          Angriffspunkt y
                        </Typography>
                      }
                      name={`results.wind_loads_per_ceiling[${direction}][${index}].point_of_attack.y`}
                    />
                  </Grid>
                </Grid>
                <Alert severity="info">
                  Windrichung ist in {windDirectionToAxis[direction].axis}{' '}
                  {windDirectionToAxis[direction].sign}
                </Alert>
                <TextField
                  label="Kommentar"
                  placeholder="Kommentar"
                  rows={2}
                  name={`results.wind_loads_per_ceiling[${direction}][${index}].comment`}
                  multiline
                />
              </Stack>
            </AccordionDetails>
          </FormAccordion>
        )
      })}
    </>
  )
}

export default FormFields
